import { useEffect, useMemo, useState } from 'react'
import { useRecoilValueLoadable } from 'recoil'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import MapBG from 'src/assets/images/map-bg.svg'
import { rgbaStrOpacity } from 'src/utils/colors'
import { PagesRenderConfig } from 'src/types/report-insights'
import { Flex } from 'src/components/flex-containers'

import * as reportInsight from 'src/atoms/report-insight'

const Page = styled.div`
  position: relative;
  outline: 1px solid ${Colors.borderInput};
  box-shadow: ${Colors.BOX_SHADOW};
  cursor: ${(props) => (!!props.onClick ? 'pointer' : 'default')};
  ${(props) =>
    !!props.onClick
      ? `
      :hover {
          outline-color: ${Colors.blueDark};
        }
      `
      : ''}
`

type CoverPageProps = {
  height: number
  width: number
  orientation: 'portrait' | 'landscape'
  title: string
  subtitle: string
  logo: string
  logoDimensions: {
    width: number
    height: number
  }
  color: string
  onClick?: () => void
}
const CoverPage = (props: CoverPageProps) => {
  const { height, width, orientation, title, subtitle, logo, logoDimensions, color, onClick } = props

  return (
    <Page
      onClick={onClick ? () => onClick() : undefined}
      style={{
        display: 'flex',
        flexDirection: orientation === 'portrait' ? 'column' : 'row',
        width,
        height,
        backgroundColor: Colors.bgWhite,
      }}>
      {/* Title section */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: reportInsight.PDF_SPACING,
        }}>
        <img src={logo} style={logoDimensions} />

        <div
          style={{
            position: orientation === 'portrait' ? 'relative' : 'absolute',
            top: orientation === 'portrait' ? '0px' : '50%',
            maxWidth: orientation === 'portrait' ? `100%` : `calc(50% - ${2 * reportInsight.PDF_SPACING}px)`,
            transform: orientation === 'portrait' ? 'none' : 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: reportInsight.PDF_SPACING,
          }}>
          <Typography
            style={{
              fontSize: 40,
              lineHeight: '40px',
              wordBreak: 'break-word',
            }}
            variant="title1"
            color={Colors.textBlack}>
            {title}
          </Typography>
          <Typography
            style={{
              fontSize: 18,
              wordBreak: 'break-word',
            }}
            variant="title2l"
            color={Colors.textDark}>
            {subtitle}
          </Typography>
        </div>
      </div>

      {/* Map Section */}
      <div
        style={{
          flex: orientation === 'portrait' ? 2 : 1,
          padding: reportInsight.PDF_SPACING,
          background: `
          linear-gradient(0deg, ${rgbaStrOpacity(Colors.blueDark, 0.2)},
          ${rgbaStrOpacity(Colors.blueDark, 0.2)}), url(${MapBG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderTop: orientation === 'portrait' ? `2px solid ${color}` : 'none',
          borderLeft: orientation === 'landscape' ? `2px solid ${color}` : 'none',
        }}
      />
    </Page>
  )
}

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`

const PageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${Colors.bgWhite};
  border-top: 2px solid ${Colors.blueDark};
`

type PageFooterElementProps = {
  logo: string
  logoDimensions: {
    width: number
    height: number
  }
  color: string
  pageNumber: number
  style: React.CSSProperties
}
const PageFooterElement = (props: PageFooterElementProps) => {
  const { logo, logoDimensions, color, pageNumber, style } = props
  return (
    <PageFooter key={`${pageNumber}-footer`} style={{ ...style, borderTopColor: color }}>
      <img src={logo} style={logoDimensions} />

      <Typography
        style={{
          color: Colors.textDark,
        }}>
        {pageNumber}
      </Typography>
    </PageFooter>
  )
}

type InsightDownloadPreviewProps = {
  pageNumberZoom?: number
  onClickPagePreview?: (page: number) => void
}
export const InsightDownloadPreview = (props: InsightDownloadPreviewProps) => {
  const { pageNumberZoom, onClickPagePreview } = props
  const renderConfig = useRecoilValueLoadable(reportInsight.renderConfig)
  const [cachedConfig, setCachedConfig] = useState<Partial<PagesRenderConfig>>({ pages: [] })
  useEffect(() => {
    if (renderConfig.state === 'hasValue') {
      setCachedConfig(renderConfig.contents)
    }
  }, [renderConfig])

  const renderedPages = useMemo(() => {
    const { pages, page: pageConfig, coverPage, logoDimensions } = cachedConfig
    if (!pages || !pageConfig || !logoDimensions) {
      return null
    }
    const allPages = []
    const basePageNumber = coverPage ? 1 : 0
    // Insert cover page
    if (coverPage) {
      allPages.unshift(
        <Flex key={'cover-page'} align="center" gap={Shapes.spacing[4] * (pageNumberZoom || 1)}>
          {pageNumberZoom && (
            <Typography
              variant="title2l"
              color={Colors.textBlack}
              style={{
                transform: `scale(${pageNumberZoom})`,
              }}>
              1
            </Typography>
          )}
          <CoverPage
            onClick={onClickPagePreview ? () => onClickPagePreview(0) : undefined}
            height={pageConfig.height}
            width={pageConfig.width}
            orientation={pageConfig.orientation}
            title={coverPage.title}
            subtitle={coverPage.subtitle}
            logo={coverPage.logo}
            logoDimensions={logoDimensions}
            color={coverPage.color}
          />
        </Flex>
      )
    }
    pages.forEach((page, pageIndex) => {
      const currentPage = page.map((pageElement, pageElementIndex) => {
        const { type, width, height, xPosition, yPosition } = pageElement
        switch (type) {
          case 'header':
            return (
              <PageHeader
                key={`${pageIndex}-${pageElementIndex}-header`}
                style={{
                  position: 'absolute',
                  left: xPosition,
                  top: yPosition,
                  width,
                  height,
                }}>
                <Typography
                  style={{
                    color: pageElement.title.fontColor,
                    fontSize: pageElement.title.fontSize,
                  }}>
                  {pageElement.title.text}
                </Typography>
                <Typography
                  style={{
                    color: pageElement.subtitle.fontColor,
                    fontSize: pageElement.subtitle.fontSize,
                  }}>
                  {pageElement.subtitle.text}
                </Typography>
              </PageHeader>
            )
          case 'image': {
            if (!pageElement.image) {
              return null
            }
            return (
              <div
                key={`${pageIndex}-${pageElementIndex}-image`}
                style={{
                  position: 'absolute',
                  left: xPosition,
                  top: yPosition,
                  width,
                  height,
                  backgroundImage: `url(${pageElement.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )
          }
          case 'footer':
            return (
              <PageFooterElement
                key={`${pageIndex}-${pageElementIndex}-footer`}
                style={{
                  position: 'absolute',
                  left: xPosition,
                  top: yPosition,
                  width,
                  height,
                  maxHeight: height,
                  paddingInline: reportInsight.PDF_SPACING,
                  backgroundColor: Colors.bgWhite,
                }}
                logo={pageElement.logo}
                logoDimensions={logoDimensions}
                color={pageElement.color}
                pageNumber={pageElement.pageNumber}
              />
            )
          default:
            return null
        }
      })
      const pg = (
        <Flex key={pageIndex + basePageNumber + 'page'} align="center" gap={Shapes.spacing[4] * (pageNumberZoom || 1)}>
          {pageNumberZoom && (
            <Typography
              variant="title2l"
              color={Colors.textBlack}
              style={{
                transform: `scale(${pageNumberZoom})`,
              }}>
              {allPages.length + 1}
            </Typography>
          )}
          <Page
            onClick={onClickPagePreview ? () => onClickPagePreview(pageIndex + basePageNumber) : undefined}
            style={{
              width: pageConfig.width,
              height: pageConfig.height,
              backgroundColor: pageConfig.color,
            }}>
            {currentPage}
          </Page>
        </Flex>
      )
      allPages.push(pg)
    })

    return allPages
  }, [cachedConfig])
  return <>{renderedPages}</>
}
